import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    Tabs,
    Form,
    Input,
    Button,
    Spin,
    message,
    Modal,
    Row,
    Col,
    Select,
    InputNumber,
    Table,
    Checkbox
} from "antd";
import { api } from "@redux/api";
import { FileManagement } from "@components/FileManagement";
import { useSelector } from "react-redux";
import moment from "moment";
import notification2 from "@components/Notification2";
import CustomDatePicker from "@components/CustomDatePicker";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import "./index.css";

const { TabPane } = Tabs;
const { TextArea } = Input;
const { Option } = Select;

const FunctionalTestingDetail = ({ isAdd = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();
    const { closeTab, refreshTab } = useKeepAliveContext();

    // Get templateId from URL query parameters
    const searchParams = new URLSearchParams(location.search);
    const templateId = searchParams.get('templateId');
    const equipmentIdParam = searchParams.get("equipmentId") || location.state?.equipmentId;
    const preselectedEquipmentId =
        equipmentIdParam !== null &&
        equipmentIdParam !== undefined &&
        equipmentIdParam !== "" &&
        !Number.isNaN(Number(equipmentIdParam))
            ? Number(equipmentIdParam)
            : equipmentIdParam;

    // Determine if this is a create page
    const isCreatePage = isAdd || location.pathname === "/facility/create-functional-testing";

    const [loading, setLoading] = useState(isCreatePage ? false : true);
    const [readOnly, setReadOnly] = useState(!isCreatePage);
    const [testingData, setTestingData] = useState(null);
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [templateOptions, setTemplateOptions] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showTemplateSelector, setShowTemplateSelector] = useState(isCreatePage && !templateId);
    const [activeTabKey, setActiveTabKey] = useState("1");

    const userName = useSelector((state) =>
        state?.Auth?.user || state?.Auth?.first_name + " " + state?.Auth?.last_name
    );

    // Helper function to safely parse value field
    const parseValueField = (value) => {
        if (!value) return [];

        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch (e) {
                console.error("Error parsing value:", e);
                return [];
            }
        } else if (Array.isArray(value)) {
            return value;
        }

        return [];
    };

    const parseTemplateFields = (template) => {
        if (!template?.fields) return [];

        if (typeof template.fields === "string") {
            try {
                return JSON.parse(template.fields);
            } catch (e) {
                console.error("Error parsing template fields:", e);
                return [];
            }
        }

        return Array.isArray(template.fields) ? template.fields : [];
    };

    const buildDefaultTestItems = (fields) => {
        return fields.map((field) => ({
            type: field.type || "",
            name: field.name || "",
            description: field.description || "",
            SetPoint: "",
            BMSValue: "",
            MeasuredValue: "",
            Acceptable: false,
            Comment: "",
        }));
    };

    useEffect(() => {
        if (isCreatePage) {
            fetchEquipmentList(preselectedEquipmentId);
            fetchTemplateList();
            // If templateId is provided in URL, load template data
            if (templateId) {
                fetchTemplateById(templateId);
            }
        } else if (id) {
            fetchTestingDetail();
            fetchEquipmentList();
        }
    }, [id, isCreatePage, templateId, preselectedEquipmentId]);

    const fetchTestingDetail = async () => {
        try {
            setLoading(true);
            const response = await api.filterFunctionalTestingView(`id=${id}`);

            if (response.status === 200 && response.data.results.length > 0) {
                const data = response.data.results[0];
                setTestingData(data);

                // Parse the value field (JSON array) - handle both string and object
                const valueData = parseValueField(data.value);

                // Set form values
                const formValues = {
                    ...data,
                    record_date: data.record_date ? moment(data.record_date) : null,
                    value: valueData,
                };
                form.setFieldsValue(formValues);
            } else {
                message.error("Functional Testing not found");
            }
        } catch (error) {
            console.error("Error fetching functional testing:", error);
            message.error("Failed to load functional testing details");
        } finally {
            setLoading(false);
        }
    };

    const fetchEquipmentList = async (defaultEquipmentId) => {
        try {
            const response = await api.filterEquipment("page=1&page_size=999");
            if (response.status === 200) {
                const equipmentList = response.data.results || [];
                setEquipmentOptions(equipmentList);

                if (
                    defaultEquipmentId !== null &&
                    defaultEquipmentId !== undefined &&
                    defaultEquipmentId !== ""
                ) {
                    const matchedEquipment = equipmentList.find(
                        (equipment) => `${equipment.id}` === `${defaultEquipmentId}`
                    );

                    form.setFieldsValue({
                        equipment: matchedEquipment ? matchedEquipment.id : defaultEquipmentId,
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching equipment list:", error);
        }
    };

    const fetchTemplateList = async () => {
        try {
            // Fetch all templates (or filter by equipment category if needed)
            const response = await api.filterFunctionalFormView("page=1&page_size=999");
            if (response.status === 200) {
                const templates = response.data.results || [];
                const options = [
                    { id: "", name: "Blank Template (Default)", fields: [] },
                    ...templates,
                ];
                setTemplateOptions(options);
            }
        } catch (error) {
            console.error("Error fetching template list:", error);
        }
    };

    const fetchTemplateById = async (templateId) => {
        try {
            setLoading(true);
            const response = await api.getFunctionalFormViewById(templateId);
            
            if (response.status === 200) {
                const template = response.data?.results?.[0];
                const fields = parseTemplateFields(template);
                const defaultValues = buildDefaultTestItems(fields);

                form.setFieldsValue({ 
                    value: defaultValues,
                });

                if (template) {
                    setSelectedTemplate(template.id);
                    message.success("Template loaded successfully");
                }
            }
        } catch (error) {
            console.error("Error fetching template:", error);
            message.error("Failed to load template");
        } finally {
            setLoading(false);
        }
    };

    const handleTemplateSelect = (templateId) => {
        setSelectedTemplate(templateId);
    };

    const getEquipmentDisplayName = () => {
        const equipmentId = form.getFieldValue("equipment") || testingData?.equipment;
        const matchedEquipment = equipmentOptions.find(
            (equipment) => `${equipment.id}` === `${equipmentId}`
        );

        return testingData?.equipment__name || matchedEquipment?.name || "";
    };

    const handleTemplateConfirm = () => {
        if (selectedTemplate !== null) {
            const template = templateOptions.find((t) => t.id === selectedTemplate);
            if (template) {
                const defaultValues = buildDefaultTestItems(parseTemplateFields(template));
                form.setFieldsValue({ value: defaultValues });
            }
            setShowTemplateSelector(false);
        } else {
            message.warning("Please select a template");
        }
    };

    const handleEdit = () => {
        setReadOnly(false);
    };

    const handleCancel = () => {
        setReadOnly(true);
        if (testingData) {
            // Parse the value field - handle both string and object
            const valueData = parseValueField(testingData.value);

            const formValues = {
                ...testingData,
                record_date: testingData.record_date ? moment(testingData.record_date) : null,
                value: valueData,
            };
            form.setFieldsValue(formValues);
        }
    };

    const handleSubmit = async (values) => {
        try {
            setLoading(true);

            // Format values
            const submitValues = {
                ...values,
                record_date: values.record_date?.format
                    ? values.record_date.format("YYYY-MM-DD HH:mm:ss")
                    : values.record_date,
                value: JSON.stringify(values.value || []),
                record_by: userName,
            };

            let response;
            if (isCreatePage) {
                // Create mode
                response = await api.createFunctionalTestingView(submitValues);
            } else {
                // Edit mode
                submitValues.modifiedby_name = userName;
                response = await api.editFunctionalTestingView(id, submitValues);
            }

            if (response.status === 200 || response.status === 201) {
                notification2.success({
                    message: "Success",
                    description: isCreatePage
                        ? "Functional Testing created successfully"
                        : "Functional Testing updated successfully",
                });

                if (isCreatePage) {
                    refreshTab("/facility/functional-testing");
                    closeTab();
                    navigate("/facility/functional-testing");
                } else {
                    setReadOnly(true);
                    fetchTestingDetail();
                }
            } else {
                notification2.error({
                    message: "Error",
                    description: isCreatePage
                        ? "Failed to create functional testing"
                        : "Failed to update functional testing",
                });
            }
        } catch (error) {
            console.error(`Error ${isCreatePage ? "creating" : "updating"} functional testing:`, error);
            notification2.error({
                message: "Error",
                description: isCreatePage
                    ? "Failed to create functional testing"
                    : "Failed to update functional testing",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = () => {
        Modal.confirm({
            title: "Are you sure you want to delete this functional testing?",
            content: "This action cannot be undone.",
            okText: "Yes",
            okType: "danger",
            cancelText: "No",
            onOk: async () => {
                try {
                    setLoading(true);
                    const response = await api.deleteFunctionalTestingView(id);

                    if (response.status === 204 || response.status === 200) {
                        notification2.success({
                            message: "Success",
                            description: "Functional Testing deleted successfully",
                        });
                        refreshTab("/facility/functional-testing");
                        closeTab();
                        navigate("/facility/functional-testing");
                    } else {
                        notification2.error({
                            message: "Error",
                            description: "Failed to delete functional testing",
                        });
                    }
                } catch (error) {
                    console.error("Error deleting functional testing:", error);
                    notification2.error({
                        message: "Error",
                        description: "Failed to delete functional testing",
                    });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleCancelCreate = () => {
        closeTab();
        navigate("/facility/functional-testing");
    };

    const renderOperationButton = () => {
        if (isCreatePage) {
            return (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button onClick={handleCancelCreate}>
                        <i className="fa-solid fa-xmark pr-2"></i>Cancel
                    </Button>
                    <Button type="primary" onClick={() => form.submit()}>
                        <i className="fa-solid fa-floppy-disk pr-2"></i>Save
                    </Button>
                </div>
            );
        }

        if (readOnly) {
            return (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button type="primary" onClick={handleEdit}>
                        <i className="fa-solid fa-pen-to-square pr-2"></i>Edit
                    </Button>
                    <Button danger onClick={handleDelete}>
                        <i className="fa-solid fa-trash pr-2"></i>Delete
                    </Button>
                </div>
            );
        } else {
            return (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Button type="primary" onClick={() => form.submit()}>
                        <i className="fa-solid fa-floppy-disk pr-2"></i>Save
                    </Button>
                    <Button onClick={handleCancel}>
                        Cancel
                    </Button>
                </div>
            );
        }
    };

    // Render dynamic test items table using Form.List
    const renderTestItemsTable = () => {
        return (
            <Form.List name="value">
                {(fields, { add, remove }) => (
                    <>

                        <div style={{ marginBottom: 16 }}>
                            <Table
                                dataSource={fields}
                                pagination={false}
                                rowKey="key"
                                scroll={{ x: 1200 }}
                                columns={[
                                    {
                                        title: "Type",
                                        dataIndex: "type",
                                        width: 120,
                                        render: (_, field) => (
                                            <Form.Item
                                                name={[field.name, "type"]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                {readOnly ? (
                                                    <span>{form.getFieldValue(["value", field.name, "type"])}</span>
                                                ) : (
                                                    <Input disabled={readOnly} />
                                                )}
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "Name",
                                        dataIndex: "name",
                                        width: 150,
                                        render: (_, field) => (
                                            <Form.Item
                                                name={[field.name, "name"]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                {readOnly ? (
                                                    <span>{form.getFieldValue(["value", field.name, "name"])}</span>
                                                ) : (
                                                    <Input disabled={readOnly} />
                                                )}
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "Description",
                                        dataIndex: "description",
                                        width: 200,
                                        render: (_, field) => (
                                            <Form.Item
                                                name={[field.name, "description"]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                {readOnly ? (
                                                    <span>{form.getFieldValue(["value", field.name, "description"])}</span>
                                                ) : (
                                                    <Input disabled={readOnly} />
                                                )}
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "Set Point",
                                        dataIndex: "SetPoint",
                                        width: 120,
                                        render: (_, field) => (
                                            <Form.Item
                                                name={[field.name, "SetPoint"]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                {readOnly ? (
                                                    <span>{form.getFieldValue(["value", field.name, "SetPoint"])}</span>
                                                ) : (
                                                    <Input disabled={readOnly} />
                                                )}
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "BMS Value",
                                        dataIndex: "BMSValue",
                                        width: 120,
                                        render: (_, field) => (
                                            <Form.Item
                                                name={[field.name, "BMSValue"]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                {readOnly ? (
                                                    <span>{form.getFieldValue(["value", field.name, "BMSValue"])}</span>
                                                ) : (
                                                    <Input disabled={readOnly} />
                                                )}
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "Measured Value",
                                        dataIndex: "MeasuredValue",
                                        width: 140,
                                        render: (_, field) => (
                                            <Form.Item
                                                name={[field.name, "MeasuredValue"]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                {readOnly ? (
                                                    <span>{form.getFieldValue(["value", field.name, "MeasuredValue"])}</span>
                                                ) : (
                                                    <Input disabled={readOnly} />
                                                )}
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "Acceptable",
                                        dataIndex: "Acceptable",
                                        width: 100,
                                        align: "center",
                                        render: (_, field) => (
                                            <Form.Item
                                                name={[field.name, "Acceptable"]}
                                                valuePropName="checked"
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Checkbox disabled={readOnly} />
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "Comment",
                                        dataIndex: "Comment",
                                        width: 200,
                                        render: (_, field) => (
                                            <Form.Item
                                                name={[field.name, "Comment"]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                {readOnly ? (
                                                    <span>{form.getFieldValue(["value", field.name, "Comment"])}</span>
                                                ) : (
                                                    <TextArea rows={1} disabled={readOnly} />
                                                )}
                                            </Form.Item>
                                        ),
                                    },
                                    {
                                        title: "",
                                        width: 60,
                                        align: "center",
                                        render: (_, field) =>
                                            !readOnly && (
                                                <Button
                                                    type="link"
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => remove(field.name)}
                                                />
                                            ),
                                    },
                                ]}
                            />
                        </div>

                        {!readOnly && <div style={{ marginBottom: 16 }}><Button
                            type="dashed"
                            onClick={() =>
                                add({
                                    type: "",
                                    name: "",
                                    description: "",
                                    SetPoint: "",
                                    BMSValue: "",
                                    MeasuredValue: "",
                                    Acceptable: false,
                                    Comment: "",
                                })
                            }
                            block
                            icon={<PlusOutlined />}
                            disabled={readOnly}
                        >
                            Add Test Item
                        </Button>
                        </div>
                        }
                    </>
                )}
            </Form.List>
        );
    };

    // Render form fields
    const renderFormFields = () => {
        return (
            <Form
                form={form}
                onFinish={handleSubmit}
                layout="horizontal"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 16 }}
                className="detailForm"
            >
                <Row gutter={[0, 0]}>
                    {/* ID - Hidden in create mode */}
                    {!isCreatePage && (
                        <Col xs={24} lg={12}>
                            <Form.Item label="ID" name="id">
                                <Input disabled />
                            </Form.Item>
                        </Col>
                    )}

                    {/* Equipment */}
                    <Col xs={24} lg={12}>
                        {readOnly ? (
                            <Form.Item label="Equipment">
                                <Input disabled value={getEquipmentDisplayName()} />
                            </Form.Item>
                        ) : (
                            <Form.Item
                                label="Equipment"
                                name="equipment"
                                rules={[{ required: true, message: "This field is required" }]}
                            >
                                <Select
                                    disabled={readOnly}
                                    placeholder="Select Equipment"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.children ?? "")
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                    }
                                    dropdownRender={(menu) => (
                                        <>
                                            {menu}
                                             <div
                                                 style={{
                                                     borderTop: '1px solid #f0f0f0',
                                                     padding: '8px',
                                                     cursor: 'pointer',
                                                 }}
                                             >
                                                 <Button
                                                     type="primary"
                                                     icon={<i className="fa-solid fa-plus pr-1"></i>}
                                                     onClick={() => {
                                                         navigate('/facility/create-equipment');
                                                     }}
                                                     style={{ width: '100%', textAlign: 'left' }}
                                                 >
                                                     New Equipment
                                                 </Button>
                                             </div>
                                        </>
                                    )}
                                >
                                    {equipmentOptions.map((eq) => (
                                        <Option key={eq.id} value={eq.id}>
                                            {eq.name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        )}
                    </Col>

                    {/* Test Date */}
                    <Col xs={24} lg={12}>
                        <Form.Item
                            label="Test Date"
                            name="record_date"
                            rules={[{ required: true, message: "This field is required" }]}
                        >
                            <CustomDatePicker
                                format="YYYY-MM-DD"
                                style={{ width: "100%" }}
                                disabled={readOnly}
                            />
                        </Form.Item>
                    </Col>

                    {/* Test By */}
                    <Col xs={24} lg={12}>
                        <Form.Item label="Test By" name="record_by">
                            <Input disabled={readOnly} autoComplete="off" allowClear />
                        </Form.Item>
                    </Col>

                    {/* OA Temp */}
                    <Col xs={24} lg={12}>
                        <Form.Item label="OA Temp (°F)" name="oa_temp">
                            <InputNumber disabled={readOnly} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>

                    {/* OA RH */}
                    <Col xs={24} lg={12}>
                        <Form.Item label="OA RH (%)" name="oa_rh">
                            <InputNumber disabled={readOnly} style={{ width: "100%" }} />
                        </Form.Item>
                    </Col>

                    {/* Description - Full Width */}
                    <Col xs={24} lg={12}>
                        <Form.Item
                            label="Summary"
                            name="description"
                        >
                            <TextArea rows={3} disabled={readOnly} />
                        </Form.Item>
                    </Col>

                    {/* Test Items Table - Full Width */}
                    <Col xs={24}>
                            {renderTestItemsTable()}
                    </Col>

                    {/* Audit Fields - Hidden in create mode */}
                    {!isCreatePage && (
                        <>
                            <Col xs={24} lg={12}>
                                <Form.Item label="Created By" name="createdby_name">
                                    <span>{testingData?.createdby_name}</span>
                                </Form.Item>
                            </Col>

                            <Col xs={24} lg={12}>
                                <Form.Item label="Created Date" name="created_date">
                                    <span>
                                        {testingData?.created_date
                                            ? moment(testingData.created_date).format("YYYY-MM-DD HH:mm:ss")
                                            : ""}
                                    </span>
                                </Form.Item>
                            </Col>

                            <Col xs={24} lg={12}>
                                <Form.Item label="Modified By" name="lastModified_name">
                                    <span>{testingData?.lastModified_name}</span>
                                </Form.Item>
                            </Col>

                            <Col xs={24} lg={12}>
                                <Form.Item label="Modified Date" name="modified_date">
                                    <span>
                                        {testingData?.modified_date
                                            ? moment(testingData.modified_date).format("YYYY-MM-DD HH:mm:ss")
                                            : ""}
                                    </span>
                                </Form.Item>
                            </Col>
                        </>
                    )}
                </Row>
            </Form>
        );
    };

    // Template Selector Modal
    const renderTemplateSelector = () => {
        return (
            <Modal
                title="Select a Template for Functional Testing"
                open={showTemplateSelector}
                onOk={handleTemplateConfirm}
                onCancel={() => setShowTemplateSelector(false)}
                closable={false}
                width={520}
                okText="Next"
            >
                <div style={{ paddingTop: "2rem", paddingBottom: "2rem" }}>
                    <Select
                        style={{ width: "100%" }}
                        placeholder="Choose a Template"
                        value={selectedTemplate}
                        onChange={handleTemplateSelect}
                    >
                        {templateOptions.map((template) => (
                            <Option key={template.id} value={template.id}>
                                {template.name}
                            </Option>
                        ))}
                    </Select>
                </div>
            </Modal>
        );
    };

    if (loading && !testingData && !isCreatePage) {
    return (
            <div style={{ textAlign: "center", padding: "100px 0" }}>
                <Spin size="large" />
        </div>
        );
    }

    return (
        <div className="functional-testing-detail-container">
            <Spin spinning={loading}>
                {isCreatePage && renderTemplateSelector()}

                <Tabs 
                    defaultActiveKey="1" 
                    destroyInactiveTabPane={true}
                    activeKey={activeTabKey}
                    onChange={setActiveTabKey}
                    tabBarExtraContent={activeTabKey === "1" ? renderOperationButton() : null}
                >
                    <TabPane
                        tab={
                            <span>
                                <i className="fa-regular fa-rectangle-list pr-2"></i>Detail
                            </span>
                        }
                        key="1"
                    >
                        <div className="slds-m-around_x-small">
                            <div className="px-4">
                                <div className="w-full">{renderFormFields()}</div>
                            </div>
                        </div>
                    </TabPane>

                    {/* Attachments tab - Hidden in create mode */}
                    {!isCreatePage && (
                        <TabPane
                            tab={
                                <span>
                                    <i className="fa-solid fa-file-pdf pr-2"></i>Attachments
                                </span>
                            }
                            key="2"
                        >
                            {testingData ? (
                                <FileManagement
                                    tableName="functiontesting"
                                    recordId={id}
                                    equipmentId={testingData?.equipment}
                                />
                            ) : (
                                ""
                            )}
                        </TabPane>
                    )}
                </Tabs>
            </Spin>
        </div>
    );
};

export default FunctionalTestingDetail;
